import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Available header width when the inline menu reached its minimum
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /** @type {number | null} */
  #headerWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    const size = entry?.borderBoxSize?.[0] ?? entry?.borderBoxSize;
    if (!size || !Number.isFinite(size.blockSize)) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // Avoid invalidating styles when only the header's width changed.
    const height = `${Math.round(size.blockSize)}px`;
    if (document.body.style.getPropertyValue('--header-height') !== height) {
      document.body.style.setProperty('--header-height', height);
    }
    this.#headerWidth = size.inlineSize;

    // Re-evaluate the actual header space throughout drawer open/close animations.
    // The browser window does not resize when the page is squeezed beside a drawer.
    if (this.#menuDrawerHiddenWidth && size.inlineSize > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    } else {
      setHeaderMenuStyle(this.#headerWidth);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = this.#headerWidth ?? this.getBoundingClientRect().width;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle(this.#headerWidth);
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = getScrollTop();
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this, { box: 'border-box' });
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#headerWidth = null;
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

// Anchor the shopify-account dialog to its trigger instead of the viewport edge.
document.addEventListener(
  'open',
  (event) => {
    const { target } = event;
    if (!(target instanceof HTMLElement) || !target.matches('shopify-account')) return;

    const button = target.closest('.account-button') ?? target;
    const updatePosition = () => {
      const { bottom, left, right } = button.getBoundingClientRect();
      const inlineEnd =
        getComputedStyle(target).direction === 'rtl' ? left : document.documentElement.clientWidth - right;

      target.style.setProperty('--account-dialog-top', `${Math.max(0, Math.round(bottom))}px`);
      target.style.setProperty('--account-dialog-inline-end', `${Math.max(20, Math.round(inlineEnd))}px`);
    };

    updatePosition();

    // Keep the open panel attached when the viewport or sticky header moves.
    // Listen in capture phase to include Horizon's desktop page scroll container.
    const controller = new AbortController();
    window.addEventListener('resize', updatePosition, { signal: controller.signal });
    document.addEventListener('scroll', updatePosition, { capture: true, passive: true, signal: controller.signal });

    // A desktop drawer resizes the page without resizing the browser window.
    const resizeObserver = new ResizeObserver(updatePosition);
    resizeObserver.observe(button);
    const pageWrapper = target.closest('.page-wrapper');
    if (pageWrapper) resizeObserver.observe(pageWrapper);

    target.addEventListener(
      'close',
      () => {
        controller.abort();
        resizeObserver.disconnect();
      },
      { once: true, signal: controller.signal }
    );
  },
  true
);

onDocumentLoaded(() => {
  const headerGroup = document.querySelector('#header-group');
  if (!headerGroup) return;

  // ResizeObserver only reports changed targets, not the whole group. Retain
  // every size so resizing an announcement never drops the header's height.
  // Keep the inline first-paint measurement until all new targets have reported.
  const heights = new Map();
  let children = [];
  let header = null;

  const updateHeight = () => {
    if (children.some((child) => heights.get(child) === null)) return;
    let height = children.reduce((total, child) => total + (heights.get(child) ?? 0), 0);
    if (header?.hasAttribute('transparent') && header.parentElement?.nextElementSibling) {
      if (heights.get(header) === null) return;
      height += heights.get(header) ?? 0;
    }
    const value = `${Math.round(height)}px`;
    if (document.body.style.getPropertyValue('--header-group-height') !== value) {
      document.body.style.setProperty('--header-group-height', value);
    }
  };

  const resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (!heights.has(entry.target)) continue;
      const size = entry.borderBoxSize?.[0] ?? entry.borderBoxSize;
      heights.set(entry.target, size?.blockSize ?? entry.target.offsetHeight);
    }
    updateHeight();
  });

  const syncTargets = () => {
    children = Array.from(headerGroup.children).filter((child) => child instanceof HTMLElement);
    header = headerGroup.querySelector('header-component');
    const targets = new Set(children);
    if (header instanceof HTMLElement) targets.add(header);
    for (const target of heights.keys()) {
      if (targets.has(target)) continue;
      resizeObserver.unobserve(target);
      heights.delete(target);
    }
    for (const target of targets) {
      if (heights.has(target)) continue;
      heights.set(target, null);
      resizeObserver.observe(target, { box: 'border-box' });
    }
    updateHeight();
  };

  syncTargets();
  new MutationObserver((mutations) => {
    if (mutations.some((mutation) =>
      mutation.type === 'attributes' || mutation.target === headerGroup ||
      mutation.target === header?.parentElement ||
      [...mutation.addedNodes, ...mutation.removedNodes].some((node) =>
        node instanceof HTMLElement && (node === header || node.querySelector('header-component'))
      )
    )) syncTargets();
  }).observe(headerGroup, { childList: true, subtree: true, attributes: true, attributeFilter: ['transparent'] });
});
